import React, { useState, useEffect } from 'react';
import { Mail, Check, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslate } from '../hooks/useTranslate';

const NewsletterSubscription = () => {
  const { currentLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [action, setAction] = useState('subscribe'); // 'subscribe' ou 'unsubscribe'
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const newsletterRef = React.useRef(null);

  // Textes français originaux
  const texts = {
    title: "Inscrivez-vous à notre newsletter",
    subtitle: "Restez informé de nos actualités, événements et publications",
    placeholder: "Votre adresse email",
    subscribe: "S'abonner",
    unsubscribe: "Se désabonner",
    subscribing: "Traitement...",
    rgpd: "En vous abonnant, vous acceptez de recevoir nos newsletters. Vous pouvez vous désabonner à tout moment.",
    errorEmail: "Veuillez entrer une adresse email valide",
    errorConnection: "Erreur de connexion au serveur"
  };

  // Traductions
  const { translatedText: translatedTitle } = useTranslate(texts.title);
  const { translatedText: translatedSubtitle } = useTranslate(texts.subtitle);
  const { translatedText: translatedPlaceholder } = useTranslate(texts.placeholder);
  const { translatedText: translatedSubscribe } = useTranslate(texts.subscribe);
  const { translatedText: translatedUnsubscribe } = useTranslate(texts.unsubscribe);
  const { translatedText: translatedSubscribing } = useTranslate(texts.subscribing);
  const { translatedText: translatedRgpd } = useTranslate(texts.rgpd);
  const { translatedText: translatedErrorEmail } = useTranslate(texts.errorEmail);
  const { translatedText: translatedErrorConnection } = useTranslate(texts.errorConnection);

  // Utiliser les traductions si la langue n'est pas français
  const displayTexts = currentLanguage === 'fr' ? texts : {
    title: translatedTitle || texts.title,
    subtitle: translatedSubtitle || texts.subtitle,
    placeholder: translatedPlaceholder || texts.placeholder,
    subscribe: translatedSubscribe || texts.subscribe,
    unsubscribe: translatedUnsubscribe || texts.unsubscribe,
    subscribing: translatedSubscribing || texts.subscribing,
    rgpd: translatedRgpd || texts.rgpd,
    errorEmail: translatedErrorEmail || texts.errorEmail,
    errorConnection: translatedErrorConnection || texts.errorConnection
  };

  // Détecter le paramètre ?unsubscribe=true dans l'URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const unsubscribeParam = urlParams.get('unsubscribe');
    
    if (unsubscribeParam === 'true') {
      // Pré-sélectionner l'option "Se désabonner"
      setAction('unsubscribe');
      
      // Scroll vers le formulaire après un court délai
      setTimeout(() => {
        if (newsletterRef.current) {
          newsletterRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setMessage({ type: 'error', text: displayTexts.errorEmail });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const endpoint = action === 'subscribe' 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`
        : `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/unsubscribe`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: data.message });
        setEmail('');
      } else {
        setMessage({ type: 'error', text: data.message || 'Une erreur est survenue' });
      }
    } catch (error) {
      console.error('Erreur:', error);
      setMessage({ type: 'error', text: displayTexts.errorConnection });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={newsletterRef} className="w-full py-12 px-4 bg-gray-50">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Titre */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
              <Mail className="w-8 h-8" style={{ color: 'rgb(196, 150, 96)' }} />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Lora, serif' }}>
              {displayTexts.title}
            </h2>
            <p className="text-gray-600">
              {displayTexts.subtitle}
            </p>
          </div>

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Champ email */}
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={displayTexts.placeholder}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                style={{ focusRingColor: 'rgb(196, 150, 96)' }}
                onFocus={(e) => e.target.style.outlineColor = 'rgb(196, 150, 96)'}
                disabled={loading}
              />
            </div>

            {/* Options */}
            <div className="flex items-center space-x-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="action"
                  value="subscribe"
                  checked={action === 'subscribe'}
                  onChange={(e) => setAction(e.target.value)}
                  className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                  disabled={loading}
                />
                <span className="ml-2 text-gray-700 font-medium">{displayTexts.subscribe}</span>
              </label>

              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="action"
                  value="unsubscribe"
                  checked={action === 'unsubscribe'}
                  onChange={(e) => setAction(e.target.value)}
                  className="w-4 h-4 text-red-600 focus:ring-red-500"
                  disabled={loading}
                />
                <span className="ml-2 text-gray-700 font-medium">{displayTexts.unsubscribe}</span>
              </label>
            </div>

            {/* Bouton submit */}
            <button
              type="submit"
              disabled={loading}
              style={action === 'subscribe' ? {
                borderColor: 'rgb(196, 150, 96)',
                color: 'rgb(196, 150, 96)'
              } : {
                borderColor: '#dc2626',
                color: '#dc2626'
              }}
              className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 border-2 bg-white ${
                action === 'subscribe'
                  ? 'hover:bg-amber-50'
                  : 'hover:bg-red-50'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loading ? displayTexts.subscribing : action === 'subscribe' ? displayTexts.subscribe : displayTexts.unsubscribe}
            </button>
          </form>

          {/* Messages */}
          {message.text && (
            <div className={`mt-6 p-4 rounded-lg flex items-center ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              {message.type === 'success' ? (
                <Check className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
              ) : (
                <X className="w-5 h-5 text-red-600 mr-3 flex-shrink-0" />
              )}
              <p className={`text-sm ${
                message.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {message.text}
              </p>
            </div>
          )}

          {/* Note RGPD */}
          <p className="mt-6 text-xs text-gray-500 text-center">
            {displayTexts.rgpd}
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewsletterSubscription;
